import { toEDNStringFromSimpleObject } from "edn-data";
import { queryOmegaSingleResult } from "../api";
import { Devcontainer } from "../types/devcontainer";
import { DefaultDevcontainerRecord, defaultDevcontainerPathQuery } from "../types/defaultDevcontainer";
export const getDevcontainer: (config: {
  "app-id": string; "page-id": string; "block-id": string;
}) => Promise<Devcontainer> = async (config) => {
  const appId = JSON.stringify(config["app-id"]);
  const pageId = JSON.stringify(config["page-id"]);
  const blockId = JSON.stringify(config["block-id"]);
  const query = `
        (datastore Qo.Api.Bl "omega/query-omega/api/block")
        (Qo.Api.Bl/get-data-by-id ${appId} ${pageId} ${blockId} Response)
        (return [Response])`;
  return queryOmegaSingleResult(query);
};

export interface DevcontainerOption {
  name: string;
  "devcontainer-id": string;
}

// Search devcontainers through the Qo.Ds data-source protocol (U.8): the search string is the
// "name" filter on the ds-query input; Qo.Public.Api.DevCon/list-all-devcontainers forwards it to
// Qo.Ds.DevCon/ds-query, which Mango-$regex's the indexed store and returns the data source
// { header:["devcontainer"], rows:[[record],...] } (each record = {app-id,type,devcontainer-id,
// devcontainer-spec}). Map each row's record → {name, devcontainer-id}.
export const searchDevcontainers: (config: {
  "app-id": string;
  search: string;
}) => Promise<DevcontainerOption[]> = async (config) => {
  const input: Record<string, string | number> = { "app-id": config["app-id"], limit: 20 };
  if (config.search) input["name"] = config.search;
  const ednString = toEDNStringFromSimpleObject(input, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.DevCon "omega/query-omega/public/api/devcontainer")
        (Qo.Public.Api.DevCon/list-all-devcontainers ${ednString} Response)
        (return [Response])
    `;
  const ds = await queryOmegaSingleResult(query); // { header:["devcontainer"], rows:[[record],...] }
  const rows: any[][] = ds?.rows ?? [];
  return rows.map((r) => {
    const record: any = r[0];
    return {
      name: record?.["devcontainer-spec"]?.name,
      "devcontainer-id": record?.["devcontainer-id"],
    };
  });
};

// Resolve one devcontainer by id → its {name, devcontainer-id} (U.7): the SA-block picker
// trigger shows the NAME + id subtext rather than the raw UUID. Clones searchDevcontainers's
// runOql shape but invokes Qo.Public.Api.DevCon/get-devcontainer {"app-id","devcontainer-id"}
// → the devcontainer object {app-id,type,devcontainer-id,devcontainer-spec}; map its
// devcontainer-spec.name. Returns null when the id resolves to nothing.
export const getDevcontainerById: (
  appId: string,
  devcontainerId: string
) => Promise<DevcontainerOption | null> = async (appId, devcontainerId) => {
  const input: Record<string, string> = { "app-id": appId, "devcontainer-id": devcontainerId };
  const ednString = toEDNStringFromSimpleObject(input, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.DevCon "omega/query-omega/public/api/devcontainer")
        (Qo.Public.Api.DevCon/get-devcontainer ${ednString} Response)
        (return [Response])
    `;
  try {
    const record: any = await queryOmegaSingleResult(query); // {app-id,type,devcontainer-id,devcontainer-spec}
    const name = record?.["devcontainer-spec"]?.name;
    if (!name) return null;
    return { name, "devcontainer-id": devcontainerId };
  } catch {
    return null; // no solution / not found
  }
};

// The caller's Default Devcontainer Record (system:#devcontainer) through the generic read wrapper (Qo.Public.Api/path-query ->
// Qo.Path.Pq/path-query -> Qo.Pq.DefaultDevcontainer), the getHome shape (api.tsx).
export const getDefaultDevcontainer: () => Promise<DefaultDevcontainerRecord> = async () => {
  const req = toEDNStringFromSimpleObject(defaultDevcontainerPathQuery, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api "omega/query-omega/public/api")
        (Qo.Public.Api/path-query ${req} Response)
        (return [Response])
    `;
  return queryOmegaSingleResult(query);
};

// One devcontainer's {name, devcontainer-id} by its id ALONE, whatever workspace it is in (U.5): Qo.Public.Api.DevCon/get-devcontainer-by-id
// takes {"devcontainer-id"} (query-omega-api oql/app/public/api/devcontainer.oql). The Default Devcontainer may live in another workspace than the
// one the panel is open in, where getDevcontainerById (which needs the workspace) finds nothing. null when it resolves to nothing.
export const getDevcontainerNameById: (devcontainerId: string) => Promise<DevcontainerOption | null> = async (devcontainerId) => {
  const ednString = toEDNStringFromSimpleObject({ "devcontainer-id": devcontainerId }, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.DevCon "omega/query-omega/public/api/devcontainer")
        (Qo.Public.Api.DevCon/get-devcontainer-by-id ${ednString} Response)
        (return [Response])
    `;
  try {
    const record: any = await queryOmegaSingleResult(query);
    const name = record?.["devcontainer-spec"]?.name;
    return name ? { name, "devcontainer-id": devcontainerId } : null;
  } catch {
    return null;
  }
};
